import React, { useState } from 'react';
import { Edit, Trash2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { EditContactModal } from './EditContactModal';
interface ContactsTableProps {
  onContactClick?: (id: number) => void;
}
interface Contact {
  id: number;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: string;
  tags: string[];
  connectedOn: string;
  linkedin: string;
  followUp: string;
  notes: string;
}
const initialContacts: Contact[] = [{
  id: 1,
  name: 'Thomas Weber',
  company: 'TechCorp GmbH',
  email: 'thomas.weber@techcorp.de',
  phone: '+49 30 12345678',
  status: 'Kunde',
  tags: ['Kunde', 'Überfällig'],
  connectedOn: '05.01.2025',
  linkedin: '05.01.2025',
  followUp: '25.01.2025',
  notes: 'Aktiver Kunde seit 2024. Sehr zufrieden mit dem Service.'
}, {
  id: 2,
  name: 'Max Mustermann',
  company: 'Innovation AG',
  email: 'm.mustermann@innovation.de',
  phone: '+49 89 87654321',
  status: 'Interessiert',
  tags: ['Opener', 'Interessiert', 'Überfällig'],
  connectedOn: '10.01.2025',
  linkedin: '10.01.2025',
  followUp: '01.02.2025',
  notes: 'Interessiert an unseren Enterprise-Lösungen. Möchte Demo-Call vereinbaren.'
}, {
  id: 3,
  name: 'Michael Becker',
  company: 'StartUp Berlin',
  email: 'm.becker@startup.de',
  phone: '+49 30 99887766',
  status: 'Follow-up',
  tags: ['Opener', 'Interessiert', 'Follow-up', 'Überfällig'],
  connectedOn: '14.01.2025',
  linkedin: '14.01.2025',
  followUp: '20.01.2025',
  notes: 'CEO eines Startups. Sehr interessiert an Partnerschaft. Meeting nächste Woche geplant.'
}, {
  id: 4,
  name: 'Anna Schmidt',
  company: 'Digital Solutions',
  email: 'anna.schmidt@digital.de',
  phone: '+49 40 11223344',
  status: 'Erstnachricht',
  tags: ['Erstnachricht', 'Follow-up'],
  connectedOn: '12.01.2025',
  linkedin: '12.01.2025',
  followUp: '-',
  notes: 'Wartet auf Budget-Freigabe. Follow-up in 2 Wochen.'
}, {
  id: 5,
  name: 'Julia Fischer',
  company: 'Consulting Pro',
  email: 'julia.f@consulting.de',
  phone: '+49 69 55443322',
  status: 'Disqualifiziert',
  tags: ['Disqualifiziert'],
  connectedOn: '08.01.2025',
  linkedin: '08.01.2025',
  followUp: '-',
  notes: 'Kein Budget verfügbar. Möchte in 6 Monaten erneut kontaktiert werden.'
}];
const statusColors: Record<string, string> = {
  Opener: 'status-opener',
  Erstnachricht: 'status-erstnachricht',
  Interessiert: 'status-interessiert',
  Kunde: 'status-kunde',
  'Follow-up': 'status-follow-up',
  Überfällig: 'status-uberfällig',
  Disqualifiziert: 'status-disqualifiziert'
};
export function ContactsTable({
  onContactClick
}: ContactsTableProps) {
  const [contacts, setContacts] = useState<Contact[]>(initialContacts);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleEdit = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const contact = contacts.find(c => c.id === id);
    if (contact) {
      setEditingContact(contact);
      setIsModalOpen(true);
    }
  };
  const handleSave = (updatedContact: Contact) => {
    setContacts(prev => prev.map(c => c.id === updatedContact.id ? updatedContact : c));
  };
  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('Delete contact:', id);
  };
  const handleLinkedInClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('Open LinkedIn profile');
  };
  return <>
      <div className="rounded-xl bg-card border border-border overflow-hidden" data-id="element-34">
        <div className="p-6 border-b border-border flex items-center justify-between bg-[#ededed]/30 dark:bg-white/5" data-id="element-35">
          <div data-id="element-36">
            <h2 className="text-lg font-semibold text-black dark:text-white" data-id="element-37">
              Kontakte
            </h2>
            <p className="text-sm text-muted-foreground mt-1" data-id="element-38">
              Verwalten Sie Ihre Kundenkontakte
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-39">
            <Button variant="outline" size="sm" onClick={() => console.log('CSV Import')} data-id="element-40">
              CSV Import
            </Button>
            <Button variant="secondary" size="sm" onClick={() => console.log('Neuer Kontakt')} data-id="element-41">
              + Neuer Kontakt
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-42">
          <table className="w-full" data-id="element-43">
            <thead className="bg-[#ededed]/30 dark:bg-white/5" data-id="element-44">
              <tr data-id="element-45">
                <th className="text-left text-xs font-medium text-muted-foreground px-6 py-3" data-id="element-46">
                  Name
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground px-6 py-3" data-id="element-47">
                  Connected On
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground px-6 py-3" data-id="element-48">
                  LinkedIn
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground px-6 py-3" data-id="element-49">
                  Nachricht gesendet
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground px-6 py-3" data-id="element-50">
                  Follow-up
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground px-6 py-3" data-id="element-51">
                  Tags
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground px-6 py-3" data-id="element-52">
                  Notizen
                </th>
                <th className="text-left text-xs font-medium text-muted-foreground px-6 py-3" data-id="element-53">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border" data-id="element-54">
              {contacts.map(contact => <tr key={contact.id} onClick={() => onContactClick?.(contact.id)} className="hover:bg-[#ededed]/20 dark:hover:bg-white/5 transition-colors cursor-pointer" data-id="element-55">
                  <td className="px-6 py-4" data-id="element-56">
                    <div className="flex items-center gap-3" data-id="element-57">
                      <div className="h-8 w-8 rounded-full bg-[#f88d3e]/10 dark:bg-[#f88d3e]/20 flex items-center justify-center text-[#f88d3e] dark:text-[#f88d3e] font-medium text-sm" data-id="element-58">
                        {contact.name.charAt(0)}
                      </div>
                      <div data-id="element-59">
                        <div className="font-medium text-foreground" data-id="element-60">
                          {contact.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground" data-id="element-61">
                    {contact.connectedOn}
                  </td>
                  <td className="px-6 py-4" data-id="element-62">
                    <div onClick={handleLinkedInClick} className="text-sm text-[#f88d3e] flex items-center gap-1 hover:underline cursor-pointer" data-id="element-63">
                      <ExternalLink className="h-3 w-3" data-id="element-64" />
                      Profil
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground" data-id="element-65">
                    {contact.linkedin}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground" data-id="element-66">
                    {contact.followUp}
                  </td>
                  <td className="px-6 py-4" data-id="element-67">
                    <div className="flex flex-wrap gap-1" data-id="element-68">
                      {contact.tags.map(tag => <span key={tag} className={`status-badge ${statusColors[tag] || ''}`} data-id="element-69">
                          {tag}
                        </span>)}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-70">
                    <p className="text-sm text-muted-foreground max-w-xs truncate" data-id="element-71">
                      {contact.notes}
                    </p>
                  </td>
                  <td className="px-6 py-4" data-id="element-72">
                    <div className="flex items-center gap-1" data-id="element-73">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={e => handleEdit(contact.id, e)} data-id="element-74">
                        <Edit className="h-4 w-4" data-id="element-75" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={e => handleDelete(contact.id, e)} data-id="element-76">
                        <Trash2 className="h-4 w-4" data-id="element-77" />
                      </Button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <EditContactModal contact={editingContact} isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      setEditingContact(null);
    }} onSave={handleSave} data-id="element-78" />
    </>;
}